import * as React from 'react';
import { BaseInput, styled } from '@leagueplatform/genesis-core';
import type { ControlComponent } from '@web-config-app/entity-form';
import { EntityFormControl } from '@web-config-app/entity-form';

const TextAreaWrapperBase = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  flex: 1,
});

/**
 * Genesis FormField injects input-only props (id, name, aria-describedby,
 * inputStatus, autoComplete) onto its direct child via cloneElement. A plain
 * styled <div> would absorb those props onto the DOM node, breaking the
 * label↔textarea association. This wrapper forwards them to its single child
 * input so the <textarea> is the element the label actually refers to.
 */
type TextAreaWrapperProps = React.HTMLAttributes<HTMLDivElement> & {
  id?: string;
  name?: string;
  'aria-describedby'?: string;
  inputStatus?: unknown;
  autoComplete?: string;
};

const TextAreaWrapper = ({
  children,
  id,
  name,
  'aria-describedby': ariaDescribedBy,
  inputStatus,
  autoComplete,
  ...rest
}: TextAreaWrapperProps) => (
  <TextAreaWrapperBase {...rest}>
    {React.cloneElement(React.Children.only(children) as React.ReactElement, {
      id,
      name,
      'aria-describedby': ariaDescribedBy,
      inputStatus,
      autoComplete,
    })}
  </TextAreaWrapperBase>
);

/**
 * Parses a comma-separated string into an array of strings
 * @param value - The comma-separated string to parse
 * @returns Array of strings, with empty strings filtered out
 */
const parseCommaSeparatedValues = (value: string): string[] => {
  if (!value || value.trim().length === 0) {
    return [];
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
};

// Internal component that handles the state
const ArrayTextareaInput = React.forwardRef<
  HTMLTextAreaElement,
  {
    data: string[] | undefined;
    handleChange: (path: string, value: any) => void;
    path: string;
    required?: boolean;
    inputStatus?: any;
    enabled?: boolean;
    id?: string;
    name?: string;
    'aria-describedby'?: string;
    maxLength?: number;
    minLength?: number;
  }
>(
  (
    {
      data,
      handleChange,
      path,
      required,
      inputStatus,
      enabled,
      id,
      name,
      'aria-describedby': ariaDescribedBy,
      maxLength,
      minLength,
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = React.useState('');

    // Clear local text when form state clears this field.
    React.useEffect(() => {
      if (!data || data.length === 0) {
        setInputValue('');
      }
    }, [data]);

    return (
      <BaseInput
        ref={ref}
        as="textarea"
        id={id}
        name={name}
        aria-describedby={ariaDescribedBy}
        value={inputValue}
        readOnly={!enabled}
        required={required}
        minLength={minLength}
        maxLength={maxLength}
        inputStatus={inputStatus}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
          const rawValue = e.currentTarget.value;
          setInputValue(rawValue);
          const parsedArray = parseCommaSeparatedValues(rawValue);
          handleChange(path, parsedArray);
        }}
        css={{
          backgroundColor: '$inputBackgroundDefault',
          position: 'relative',
          '&:after': {
            pointerEvents: 'none',
            content: '"⟷"',
            position: 'absolute',
            textAlign: 'center',
            bottom: '6px',
            insetInlineEnd: '-2px',
            transform: 'rotate(90deg)',
          },
          '& textarea': {
            resize: 'vertical',
            '&::-webkit-resizer': {
              display: 'none',
            },
            paddingInlineEnd: '$one',
          },
        }}
        rows={4}
      />
    );
  },
);

ArrayTextareaInput.displayName = 'ArrayTextareaInput';

export const ArrayTextareaControl: ControlComponent = (props) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const setInputFocus = React.useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  return (
    <EntityFormControl
      {...props}
      renderControl={({
        data,
        handleChange,
        path,
        schema,
        required,
        enabled,
      }) => {
        const { maxLength, minLength } = schema;

        return (
          <TextAreaWrapper onClick={setInputFocus}>
            <ArrayTextareaInput
              ref={textareaRef}
              data={data}
              handleChange={handleChange}
              path={path}
              required={required}
              enabled={enabled}
              maxLength={maxLength}
              minLength={minLength}
            />
          </TextAreaWrapper>
        );
      }}
    />
  );
};
